/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { filterByKeyword, sortByProp } from '@squiz/dxp-console-support';
import {
  Button,
  EmptyState,
  ICON_INFO_WITH_LIST,
  ICON_INFO_WITH_SEARCH,
  SPINNER_TYPE_REGULAR,
  SearchFilter,
  SearchFilterProps,
  Spinner,
  Table,
  TableCell,
  TableContent,
  TableProps,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityEventSubscription, EntityEventSubscriptionService } from '../../../../Services/event-bus';
import { useEventSubscriptions } from '../../../../Services/event-bus/src/Hooks/UseEventSubscriptions/UseEventSubscriptions.hook';
import { getEventSubscriptionPath } from '../../../../Utilities/GetEventSubscriptionPath/GetEventSubscriptionPath';
import { EVENT_SUBSCRIPTIONS_DATA_IDS } from '../../EventSubscriptions.constants';

/**
 * The list of event subscriptions.
 */
export const EventSubscriptionsList = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [eventSubscriptions] = useEventSubscriptions({ service: EntityEventSubscriptionService.dxpConsole });

  const [filterValue, setFilterValue] = useState<string>(``);

  const handleNavigation = useCallback(
    (itemPath: string): void => {
      navigate(itemPath);
    },
    [navigate],
  );

  const currentScreenPath = useMemo(() => {
    return getCapabilityPath(`/event-service`);
  }, []);

  // Transform all event subscriptions into array of table rows
  const allTableRows = useMemo(() => {
    if (!eventSubscriptions) {
      return [];
    }

    return eventSubscriptions
      .sort((a, b) => sortByProp(a, b, (item) => item.name.toLowerCase()))
      .map((subscription: EntityEventSubscription): {} => {
        const { name, subscriptionid } = subscription;

        const subscriptionPath = `${currentScreenPath}${getEventSubscriptionPath(subscriptionid)}`;
        const navigateToSubscription = (event: React.MouseEvent): void => {
          event.preventDefault();
          handleNavigation(subscriptionPath);
        };

        // Add `_keywords` prop to each subscription to enable keyword filtering
        const keywords = `${name} ${subscriptionid}`;

        return {
          _config: {
            classes: `event-subscriptions__row`,
          },
          _keywords: keywords,
          id: subscriptionid,
          name: (
            <button
              className="event-subscription__name"
              data-id={EVENT_SUBSCRIPTIONS_DATA_IDS.subscriptionName}
              onClick={navigateToSubscription}
            >
              {name}
            </button>
          ),
        };
      });
  }, [currentScreenPath, eventSubscriptions, handleNavigation]);

  const filteredRows = useMemo(() => {
    return filterByKeyword(allTableRows, filterValue);
  }, [allTableRows, filterValue]);

  // Labels
  const labels = useMemo(() => {
    return {
      createEventSubscription: t(CONFIGURATION_STRINGS.createEventSubscription),
      id: t(CONFIGURATION_STRINGS.id),
      name: t(CONFIGURATION_STRINGS.name),
      noEventSubscriptionsCreated: t(CONFIGURATION_STRINGS.noEventSubscriptionsCreated),
      noMatchingEventSubscriptions: t(CONFIGURATION_STRINGS.noMatchingEventSubscriptions),
      searchPlaceholder: t(CONFIGURATION_STRINGS.searchPlaceholder),
    };
  }, [t]);

  const filterProps = {
    filterValue,
    placeholder: labels.searchPlaceholder,
    setFilterValue,
  } as SearchFilterProps;

  const createEventSubscriptionButton = useMemo((): JSX.Element => {
    return (
      <div className="event-subscription-bar__button-wrapper">
        <Button
          data-id={EVENT_SUBSCRIPTIONS_DATA_IDS.createButton}
          onClick={(): void => {
            const pathToBuilderForm = `${currentScreenPath}${getEventSubscriptionPath(`create`)}`;
            handleNavigation(pathToBuilderForm);
          }}
          text={labels.createEventSubscription}
        />
      </div>
    );
  }, [currentScreenPath, handleNavigation, labels]);

  const getEventSubscriptionsList = (): JSX.Element => {
    if (filteredRows && filteredRows.length === 0) {
      const emptyStateProps = {
        caption: labels.noMatchingEventSubscriptions,
        svg: ICON_INFO_WITH_SEARCH,
      };
      return <EmptyState {...emptyStateProps} />;
    }

    // Table setup
    const tableColumns: Array<TableCell> = [
      {
        content: labels.name,
        key: `name`,
      },
      {
        content: labels.id,
        key: `id`,
      },
    ];

    const tableContent: TableContent = { columns: tableColumns, rows: filteredRows };
    const tableProps: TableProps = {
      classes: `event-subscriptions__table`,
      perPageDefault: 20,
      perPageOptions: [20, 50, 100],
      tableContent: tableContent,
    };

    return <Table {...tableProps} />;
  };

  if (eventSubscriptions && eventSubscriptions.length === 0) {
    const emptyStateProps = {
      caption: labels.noEventSubscriptionsCreated,
      children: createEventSubscriptionButton,
      classes: `event-subscriptions--empty`,
      svg: ICON_INFO_WITH_LIST,
    };
    return <EmptyState {...emptyStateProps} />;
  }

  if (eventSubscriptions) {
    return (
      <>
        <div className="event-subscriptions-bar">
          <div className="event-subscriptions-bar__filter" data-id={EVENT_SUBSCRIPTIONS_DATA_IDS.searchBar}>
            <SearchFilter {...filterProps} />
          </div>
          {createEventSubscriptionButton}
        </div>
        {getEventSubscriptionsList()}
      </>
    );
  }

  return (
    <div className="loading">
      <Spinner type={SPINNER_TYPE_REGULAR} />
    </div>
  );
};
